import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  APP_BRAND_EVENT,
  DEFAULT_APP_BRAND,
  normalizeBrandColor,
  readAppBrand,
  saveAppBrand,
} from "../utils/appBrand";
import { APP_THEME_EVENT, saveAppTheme } from "../utils/appTheme";

const PRESETS = [
  { name: "Bill Sarthi Blue", color: "#015B9A" },
  { name: "Ocean Blue", color: "#0369A1" },
  { name: "Royal Indigo", color: "#4338CA" },
  { name: "Vibrant Violet", color: "#7C3AED" },
  { name: "Rich Plum", color: "#9333EA" },
  { name: "Ruby Rose", color: "#BE123C" },
  { name: "Berry Red", color: "#E11D48" },
  { name: "Burnt Orange", color: "#C2410C" },
  { name: "Warm Amber", color: "#B45309" },
  { name: "Fresh Emerald", color: "#047857" },
  { name: "Classic Teal", color: "#0F766E" },
  { name: "Deep Cyan", color: "#0E7490" },
  { name: "Espresso", color: "#78350F" },
  { name: "Burgundy", color: "#881337" },
  { name: "Modern Slate", color: "#374151" },
];
const DARK_THEME_ACCENT = "#3F4650";

const AppBrandPicker = ({ compact = false }) => {
  const [open, setOpen] = useState(false);
  const [darkWarningOpen, setDarkWarningOpen] = useState(false);
  const [resolvedTheme, setResolvedTheme] = useState(
    () => typeof document === "undefined" ? "light" : document.documentElement.dataset.theme || "light"
  );
  const [color, setColor] = useState(readAppBrand);
  const [draft, setDraft] = useState(readAppBrand);
  const wrapRef = useRef(null);

  useEffect(() => {
    const sync = (event) => {
      const next = event?.detail?.color || readAppBrand();
      setColor(next);
      setDraft(next);
    };
    const close = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const syncTheme = (event) => {
      const nextTheme = event?.detail?.resolved || document.documentElement.dataset.theme || "light";
      setResolvedTheme(nextTheme);
      if (nextTheme === "dark") setOpen(false);
    };
    window.addEventListener(APP_BRAND_EVENT, sync);
    window.addEventListener(APP_THEME_EVENT, syncTheme);
    window.addEventListener("storage", sync);
    document.addEventListener("pointerdown", close);
    return () => {
      window.removeEventListener(APP_BRAND_EVENT, sync);
      window.removeEventListener(APP_THEME_EVENT, syncTheme);
      window.removeEventListener("storage", sync);
      document.removeEventListener("pointerdown", close);
    };
  }, []);

  const commit = (value) => {
    const next = normalizeBrandColor(value);
    setColor(next);
    setDraft(next);
    saveAppBrand(next);
  };

  const handleTrigger = () => {
    if (resolvedTheme === "dark") {
      setOpen(false);
      setDarkWarningOpen(true);
      return;
    }
    setOpen((value) => !value);
  };

  const switchToLightAndOpen = () => {
    saveAppTheme("light");
    setResolvedTheme("light");
    setDarkWarningOpen(false);
    window.setTimeout(() => setOpen(true), 0);
  };

  return (
    <Wrap ref={wrapRef}>
      <Trigger type="button" onClick={handleTrigger} title="Change brand color" $compact={compact}>
        <Swatch $color={resolvedTheme === "dark" ? DARK_THEME_ACCENT : color} /> {!compact && "Brand"}
      </Trigger>
      {open && (
        <Panel role="dialog" aria-label="Brand color settings">
          <strong>Primary color</strong>
          <small>Applies to Light mode. Dark mode keeps the default colors.</small>
          <PickerRow>
            <input type="color" value={color} onChange={(event) => commit(event.target.value)} />
            <input
              value={draft}
              maxLength={7}
              onChange={(event) => setDraft(event.target.value.toUpperCase())}
              onBlur={() => commit(draft)}
              onKeyDown={(event) => event.key === "Enter" && commit(draft)}
              aria-label="Primary color hex value"
            />
          </PickerRow>
          <PresetRow>{PRESETS.map((preset) => (
            <Preset
              key={preset.color}
              type="button"
              $color={preset.color}
              $active={preset.color === color}
              onClick={() => commit(preset.color)}
              aria-label={`Use ${preset.name} ${preset.color}`}
              title={`${preset.name} · ${preset.color}`}
            />
          ))}</PresetRow>
          <Reset type="button" onClick={() => commit(DEFAULT_APP_BRAND)}>Reset default</Reset>
        </Panel>
      )}
      {darkWarningOpen && (
        <ModalOverlay role="presentation" onMouseDown={() => setDarkWarningOpen(false)}>
          <ModalCard role="dialog" aria-modal="true" aria-labelledby="brand-light-title" onMouseDown={(event) => event.stopPropagation()}>
            <ModalIcon aria-hidden="true">☀</ModalIcon>
            <h2 id="brand-light-title">Light mode required</h2>
            <p>
              Dark mode uses a fixed neutral charcoal palette.
              Please switch to Light mode to change the brand color.
            </p>
            <ModalActions>
              <ModalCancel type="button" onClick={() => setDarkWarningOpen(false)}>Stay in Dark</ModalCancel>
              <ModalPrimary type="button" onClick={switchToLightAndOpen}>Switch to Light Mode</ModalPrimary>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </Wrap>
  );
};

const Wrap = styled.div`position:relative;display:inline-flex;`;
const Trigger = styled.button`
  min-height:34px;min-width:${({$compact})=>$compact?"34px":"72px"};padding:${({$compact})=>$compact?"6px":"6px 11px"};
  border:1px solid rgba(255,255,255,.3);border-radius:999px;background:rgba(255,255,255,.12);color:#fff;
  display:inline-flex;align-items:center;justify-content:center;gap:7px;font-size:12px;font-weight:800;cursor:pointer;
`;
const Swatch = styled.span`width:14px;height:14px;border-radius:50%;background:${({$color})=>$color};border:2px solid rgba(255,255,255,.75);`;
const Panel = styled.div`
  position:absolute;right:0;top:42px;z-index:1000;width:250px;padding:14px;border-radius:14px;
  background:var(--app-surface);color:var(--app-text);border:1px solid var(--app-border);box-shadow:var(--app-shadow);
  display:grid;gap:10px;text-align:left;small{color:var(--app-text-muted);font-size:11px;}
`;
const PickerRow = styled.div`display:grid;grid-template-columns:44px 1fr;gap:8px;input{height:40px;width:100%;border:1px solid var(--app-border);border-radius:9px;background:var(--app-input-bg);color:var(--app-text);padding:5px 8px;}`;
const PresetRow = styled.div`display:grid;grid-template-columns:repeat(5,1fr);gap:9px;`;
const Preset = styled.button`
  width:30px;height:30px;justify-self:center;border-radius:50%;background:${({$color})=>$color};
  border:${({$active})=>$active?"3px solid var(--app-text)":"2px solid var(--app-border)"};cursor:pointer;
  box-shadow:${({$active})=>$active?"0 0 0 2px var(--app-surface), 0 0 0 4px var(--app-primary)":"none"};
  transition:transform .14s ease;&:hover{transform:scale(1.12)}
`;
const Reset = styled.button`border:1px solid var(--app-border);background:var(--app-surface-raised);color:var(--app-text);border-radius:9px;padding:8px;font-weight:700;cursor:pointer;`;
const ModalOverlay = styled.div`
  position:fixed;inset:0;z-index:5000;background:rgba(2,6,23,.78);backdrop-filter:blur(7px);
  display:grid;place-items:center;padding:20px;
`;
const ModalCard = styled.div`
  width:min(460px,100%);padding:28px;border-radius:22px;text-align:center;
  background:#121821;color:#edf3fb;border:1px solid #34404f;box-shadow:0 28px 80px rgba(0,0,0,.55);
  h2{margin:12px 0 8px;font-size:24px}p{margin:0;color:#b8bec7;line-height:1.6;font-size:14px}strong{color:#f1f3f5}
`;
const ModalIcon = styled.div`
  width:58px;height:58px;margin:auto;border-radius:18px;display:grid;place-items:center;
  background:#242a32;border:1px solid #59616d;color:#facc15;font-size:26px;
`;
const ModalActions = styled.div`display:grid;grid-template-columns:1fr 1.35fr;gap:10px;margin-top:22px;@media(max-width:480px){grid-template-columns:1fr}`;
const ModalCancel = styled.button`padding:12px;border-radius:12px;border:1px solid #475569;background:#202a38;color:#e7edf6;font-weight:800;cursor:pointer;`;
const ModalPrimary = styled.button`padding:12px;border-radius:12px;border:1px solid #59616d;background:#3f4650;color:#fff;font-weight:900;cursor:pointer;`;

export default AppBrandPicker;
